/* *
 * @file theme-engine.js
 * @description Fetches the active platform theme from the AscendDraft server API
 * and injects CSS variables into :root. Subscribes to Socket.IO `theme:updated`
 * for real-time cross-client sync without page reload.
 * Fallback: applies last-known theme from localStorage if API is unreachable.
 *
 * @spec F07-02-THEME-APPLICATION.md
 * @platform Static-Website (Vanilla JS / AscendDraft.com public website) */

const THEME_CACHE_KEY = "ascend-theme-cache";
const STYLE_TAG_ID = "ascend-theme-vars";

// Detect dev vs production — matches pattern used by api.js and user-sync-engine.js
const _hostname =
  typeof window !== "undefined" ? window.location.hostname : "localhost";
const _isDev =
  ["localhost", "127.0.0.1", ""].includes(_hostname) ||
  _hostname.startsWith("192.168.");

const API_BASE =
  window.ASCEND_API_URL ||
  (_isDev ? "http://localhost:5001/api" : "https://api.ascenddraft.com/api");
const SOCKET_URL =
  window.ASCEND_SOCKET_URL ||
  (_isDev ? "http://localhost:5001" : "https://api.ascenddraft.com");

/* *
 * Inject theme CSS variables into :root via a <style> tag.
 * Replaces any previously injected tag no accumulation.
 * @param {Object} theme theme document from API */
function applyThemeToDom(theme) {
  if (!theme?.colors) return;
  const c = theme.colors;

  const vars = [
    //  ==== React client-web naming (consumed by client-web CSS) ————————————————
    ["--color-primary-main", c.primary?.main],
    ["--color-primary-light", c.primary?.light],
    ["--color-primary-dark", c.primary?.dark],
    ["--color-primary-contrast", c.primary?.contrast],
    ["--color-secondary-main", c.secondary?.main],
    ["--color-secondary-light", c.secondary?.light],
    ["--color-secondary-dark", c.secondary?.dark],
    ["--color-secondary-contrast", c.secondary?.contrast],
    ["--color-success", c.success?.main],
    ["--color-success-light", c.success?.light],
    ["--color-warning", c.warning?.main],
    ["--color-warning-light", c.warning?.light],
    ["--color-error", c.error?.main],
    ["--color-error-light", c.error?.light],
    ["--color-info", c.info?.main],
    ["--color-info-light", c.info?.light],
    ["--bg-default", c.background?.default],
    ["--bg-paper", c.background?.paper],
    ["--bg-elevated", c.background?.elevated],
    ["--text-primary", c.text?.primary],
    ["--text-secondary", c.text?.secondary],
    ["--text-disabled", c.text?.disabled],
    ["--text-hint", c.text?.hint],
    ["--divider", c.divider],
    ["--tier-guest", c.tiers?.guest],
    ["--tier-bronze", c.tiers?.bronze],
    ["--tier-silver", c.tiers?.silver],
    ["--tier-gold", c.tiers?.gold],
    ["--tier-diamond", c.tiers?.diamond],
    ["--tier-enterprise", c.tiers?.enterprise],

    //  ==== Static-site token aliases (consumed by tokens.css / AscendDraft-Website) ———
    // These map the same theme model fields to the variable names the static site CSS uses.
    ["--color-primary", c.primary?.main],
    ["--color-primary-light", c.primary?.light], // same name, no conflict
    ["--color-primary-dark", c.primary?.dark], // same name, no conflict
    ["--color-bg-base", c.background?.default],
    ["--color-bg-card", c.background?.paper],
    ["--color-bg-elevated", c.background?.elevated],
    ["--color-text-primary", c.text?.primary],
    ["--color-text-secondary", c.text?.secondary],
    ["--color-text-muted", c.text?.hint],
    ["--color-text-inverse", c.primary?.contrast],
    ["--border-color", c.divider],
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");

  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = `:root {\n${vars}\n}`;
  document.documentElement.setAttribute("data-theme", theme.slug || "light");

  // Cache for offline/FOUC fallback
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(theme));
  } catch (_) {
    /* ignore */
  }
}

/* *
 * Fetch the active theme from server and apply it.
 * Falls back to localStorage cache if the API is unreachable. */
async function fetchAndApplyTheme() {
  try {
    const res = await fetch(`${API_BASE}/themes/active`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const theme = json?.data;
    if (theme) {
      applyThemeToDom(theme);
      return theme;
    }
  } catch (_) {
    // Fallback to cached theme
    try {
      const raw = localStorage.getItem(THEME_CACHE_KEY);
      if (raw) applyThemeToDom(JSON.parse(raw));
    } catch (_) {
      /* ignore */
    }
  }
  return null;
}

/* *
 * Subscribe to real-time theme:updated Socket.IO event.
 * Gracefully skips if socket.io-client is not loaded on the page.
 * Retries up to 5× (500ms apart) to handle dynamic script load timing. */
function subscribeToThemeUpdates(attempt = 0) {
  if (typeof io === "undefined") {
    // socket.io-client is still loading (dynamically injected script)
    if (attempt < 5) {
      setTimeout(() => subscribeToThemeUpdates(attempt + 1), 500);
    }
    return;
  }
  try {
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"] });
    socket.on("theme:updated", () => fetchAndApplyTheme());
    socket.on("connect_error", () => {
      /* silent theme still works via cache */
    });
  } catch (_) {
    /* ignore */
  }
}

/* *
 * Initialise the theme engine.
 * Step 1: Apply cached theme immediately to prevent FOUC.
 * Step 2: Fetch fresh theme from API asynchronously.
 * Step 3: Subscribe to real-time theme:updated events.
 *
 * @export */
export async function initThemeEngine() {
  // Deduplication guard — safe to call from multiple scripts on the SAME page load.
  // Use a session-scoped flag (not localStorage) so a fresh page load always re-applies.
  if (window.__ascendThemeInitialized) return;
  window.__ascendThemeInitialized = true;

  // Step 1 — Instant: apply cached theme (prevents flash of unstyled content)
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    if (raw) applyThemeToDom(JSON.parse(raw));
  } catch (_) {
    /* ignore */
  }

  // Step 2 — Async: fetch fresh from server (respects user's saved preference if logged in)
  await _resolveUserTheme();

  // Step 3 — Real-time: subscribe to changes
  subscribeToThemeUpdates();
}

/* *
 * Resolve which theme to show for the current user.
 * Checks the user's saved preference from /users/preferences before falling
 * back to the platform-wide active theme. */
async function _resolveUserTheme() {
  try {
    const token = _getAuthToken();
    if (token) {
      const prefRes = await fetch(`${API_BASE}/users/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (prefRes.ok) {
        const prefJson = await prefRes.json();
        const mode = prefJson?.data?.theme?.perPlatform?.static;
        if (mode === "light" || mode === "dark") {
          const res = await fetch(`${API_BASE}/themes/${mode}`);
          if (res.ok) {
            const json = await res.json();
            if (json?.data) {
              applyThemeToDom(json.data);
              return;
            }
          }
        }
      }
    }
  } catch (_) {
    /* fall through */
  }

  // Fallback 1: check localStorage preference (persisted by toggleTheme() or main.js)
  const localPref =
    localStorage.getItem("ascend-theme-pref-static") ||
    localStorage.getItem("theme"); // legacy key written by main.js initTheme()
  if (localPref === "light" || localPref === "dark") {
    try {
      const res = await fetch(`${API_BASE}/themes/${localPref}`);
      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          applyThemeToDom(json.data);
          return;
        }
      }
    } catch (_) {
      /* ignore */
    }
    // API unreachable or returned non-ok — set data-theme directly (CSS selectors handle the rest)
    document.documentElement.setAttribute("data-theme", localPref);
    return;
  }

  // Fallback 2: respect OS dark-mode preference — never override it with a platform light theme
  const osPrefDark = window.matchMedia?.(
    "(prefers-color-scheme: dark)",
  ).matches;
  if (osPrefDark) {
    try {
      const res = await fetch(`${API_BASE}/themes/dark`);
      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          applyThemeToDom(json.data);
          return;
        }
      }
    } catch (_) {
      /* ignore */
    }
    document.documentElement.setAttribute("data-theme", "dark");
    return;
  }

  // Fallback 3: platform active theme (no user or OS signal — safe to apply)
  await fetchAndApplyTheme();
}

function _getAuthToken() {
  try {
    const raw =
      localStorage.getItem("ascend_user") || localStorage.getItem("ascendUser");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.accessToken || u?.token || null;
  } catch {
    return null;
  }
}

/* *
 * Toggle between light and dark for the static site.
 * Saves the preference locally and to the API if the user is logged in.
 * Returns the new mode ('light' | 'dark').
 * @export */
export async function toggleTheme() {
  const current =
    document.documentElement.getAttribute("data-theme") || "light";
  const next = current === "dark" ? "light" : "dark";

  try {
    const res = await fetch(`${API_BASE}/themes/${next}`);
    if (res.ok) {
      const json = await res.json();
      if (json?.data) applyThemeToDom(json.data);
    }
  } catch (_) {
    /* apply by toggling data-theme only */
    document.documentElement.setAttribute("data-theme", next);
  }

  // Persist locally
  localStorage.setItem("ascend-theme-pref-static", next);

  // Persist to API if logged in
  const token = _getAuthToken();
  if (token) {
    fetch(`${API_BASE}/users/preferences/theme`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        platform: "static",
        mode: next,
        syncAcross: "current",
      }),
    }).catch(() => {});
  }

  return next;
}

/* *
 * Apply sync-across-all-platforms after user confirms in the prompt.
 * @param {string} mode 'light' | 'dark'
 * @export */
export async function syncThemeToAllPlatforms(mode) {
  const token = _getAuthToken();
  if (!token) return;
  fetch(`${API_BASE}/users/preferences/theme`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ platform: "static", mode, syncAcross: "all" }),
  }).catch(() => {});
}

export default initThemeEngine;
